import { Fragment } from 'react';
import { Tooltip } from '@odo/shared-ui';
import { TOOLTIP_OVER_MODAL } from '../../../../shared/ModalShell';
import type { MarkupTool } from './markupShapes';
import { TOOL_KEYS } from './markupShortcuts';
import { ToolIcon } from './MarkupToolIcons';

interface MarkupToolRailProps {
    tool: MarkupTool;
    onToolChange: (tool: MarkupTool) => void;
}

const TOOL_LABELS: Record<MarkupTool, string> = {
    select: 'Select',
    pen: 'Pen',
    arrow: 'Arrow',
    line: 'Line',
    rect: 'Rectangle',
    ellipse: 'Ellipse',
    text: 'Text'
};

const RAIL_TOOLS = Object.keys(TOOL_KEYS) as readonly MarkupTool[];
// Select acts on marks rather than making them, so it sits apart from the drawing tools.
const GROUP_BREAK_AFTER: MarkupTool = 'select';

const BUTTON_BASE =
    'w-10 h-10 rounded-lg inline-flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-1';
const BUTTON_ON = 'bg-gray-900 text-white shadow-sm';
const BUTTON_OFF = 'text-gray-500 hover:bg-gray-100 hover:text-gray-900';

function ToolTip({ label, shortcut }: { label: string; shortcut: string }) {
    return (
        <span className="inline-flex items-center gap-2">
            {label}
            <kbd className="min-w-[1.25rem] rounded border border-yellow-500/40 bg-white/60 px-1 text-center font-sans text-[11px] font-semibold leading-4 text-gray-700">
                {shortcut}
            </kbd>
        </span>
    );
}

function MarkupToolRail({ tool, onToolChange }: MarkupToolRailProps) {
    return (
        <div
            role="toolbar"
            aria-orientation="vertical"
            aria-label="Drawing tools"
            className="flex flex-col items-center gap-1 px-2 py-3 border-r border-gray-200 bg-white"
        >
            {RAIL_TOOLS.map((id) => {
                const pressed = id === tool;
                return (
                    <Fragment key={id}>
                        <Tooltip
                            side="right"
                            content={<ToolTip label={TOOL_LABELS[id]} shortcut={TOOL_KEYS[id]} />}
                            className={TOOLTIP_OVER_MODAL}
                        >
                            <button
                                type="button"
                                aria-label={TOOL_LABELS[id]}
                                aria-pressed={pressed}
                                aria-keyshortcuts={TOOL_KEYS[id]}
                                onClick={() => onToolChange(id)}
                                className={`${BUTTON_BASE} ${pressed ? BUTTON_ON : BUTTON_OFF}`}
                            >
                                <ToolIcon tool={id} className="w-5 h-5" />
                            </button>
                        </Tooltip>
                        {id === GROUP_BREAK_AFTER && (
                            <span aria-hidden="true" className="my-1 h-px w-6 bg-gray-200" />
                        )}
                    </Fragment>
                );
            })}
        </div>
    );
}

export { MarkupToolRail };
